package de.katoonerie.contentful

import com.apollographql.apollo3.ApolloClient
import com.apollographql.apollo3.api.ApolloResponse
import com.apollographql.apollo3.api.Optional
import de.katoonerie.AssetQuery
import de.katoonerie.HomepageQuery
import de.katoonerie.PageQuery
import de.katoonerie.contentful.ContentfulOperations.JsonNode
import de.katoonerie.http.AuthorizationInterceptor
import de.katoonerie.http.DropHeadersInterceptor
import de.katoonerie.type.Result
import de.katoonerie.util.toJsonElement
import de.katoonerie.util.toURL
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.decodeFromJsonElement
import org.jetbrains.compose.web.css.Color
import org.w3c.dom.url.URL

class ContentfulClient(
    url: URL,
    private val token: Token
) :
    ContentfulOperations.GetHomepage,
    ContentfulOperations.GetPage,
    ContentfulOperations.GetAsset {

    private val previewHeader = "X-CONTENTFUL-PREVIEW"

    data class Token(
        val delivery: Delivery,
        val preview: Preview
    ) {
        value class Delivery(val value: String)
        value class Preview(val value: String)
    }

    private val apolloClient: ApolloClient = ApolloClient.Builder()
        .serverUrl(url.toString())
        .addHttpInterceptor(
            AuthorizationInterceptor { request ->
                when (request.headers.any { it.name == previewHeader }) {
                    true -> token.preview.value
                    else -> token.delivery.value
                }
            }
        )
        .addHttpInterceptor(
            DropHeadersInterceptor(
                previewHeader,
                "X-APOLLO-OPERATION-ID",
                "X-APOLLO-OPERATION-NAME"
            )
        )
        .build()

    private fun ApolloClient.preview(preview: Boolean): ApolloClient = when (preview) {
        true -> this.newBuilder().addHttpHeader(previewHeader, "true").build()
        else -> this
    }

    override suspend fun getHomepage(preview: Boolean): Result<Homepage> {
        return try {
            val response = apolloClient.preview(preview)
                .query(HomepageQuery(preview = Optional.present(preview)))
                .execute()
            val homepage = response.toHomepage(preview)
            Result.Success(homepage)
        } catch (e: Throwable) {
            e.printStackTrace()
            Result.Error
        }
    }

    override suspend fun getPageByUrl(url: String, preview: Boolean): Result<Page?> {
        return try {
            val response = apolloClient.preview(preview)
                .query(PageQuery(url = url, preview = Optional.present(preview)))
                .execute()
            val page = response.toPage(preview)
            Result.Success(page)
        } catch (e: Throwable) {
            e.printStackTrace()
            Result.Error
        }
    }

    override suspend fun getAsset(id: String, preview: Boolean): Result<Asset?> {
        return try {
            val response = apolloClient.preview(preview)
                .query(AssetQuery(id = id, preview = Optional.present(preview)))
                .execute()
            val asset = Asset(
                url = URL(response.data!!.asset!!.url!!),
                title = response.data!!.asset!!.title!!
            )
            Result.Success(asset)
        } catch (e: Throwable) {
            e.printStackTrace()
            Result.Error
        }
    }

    private suspend fun ApolloResponse<HomepageQuery.Data>.toHomepage(preview: Boolean): Homepage =
        when (val homepage = data?.homepageCollection?.items?.first()) {
            is HomepageQuery.Item -> {
                Homepage(
                    bannerUrl = homepage.banner?.url?.toURL() ?: URL("data://"),
                    colors = Homepage.Colors(
                        background = Color(
                            homepage.backgroundColor?.hex ?: "white"
                        ),
                        accent = Color(homepage.accentColor?.hex ?: "white")
                    ),
                    navigationBars = homepage.navigationBarsCollection?.items?.filterNotNull()
                        ?.map { navigationBar ->
                            NavigationBar(
                                page = NavigationBar.Page(
                                    path = navigationBar.page?.url
                                        ?: "home-navbar-page-path-missing",
                                    title = navigationBar.page?.name
                                        ?: "home navbar page name missing"
                                ),
                                items = navigationBar.navigationItemsCollection?.items?.filterNotNull()
                                    ?.map { item ->
                                        NavigationBar.Item(
                                            tile = NavigationBar.Item.Tile(
                                                title = item.tile?.name
                                                    ?: "home nav item tile name missing",
                                                hash = item.tile?.slug
                                                    ?: "nome-nav-item-tile-slug-missing"
                                            ),
                                            color = Color(
                                                item.color?.hex
                                                    ?: "blue"
                                            ),
                                            iconUrl = item.icon?.url?.toURL()
                                                ?: URL("data://")
                                        )
                                    } ?: emptyList()
                            )
                        } ?: emptyList(),
                    actionsBar = homepage.actionsBar?.let { actionsBar ->
                        NavigationBar(
                            page = NavigationBar.Page(
                                path = actionsBar.page?.url
                                    ?: "home-actionsbar-page-path-missing",
                                title = actionsBar.page?.name
                                    ?: "home actions page name missing"
                            ),
                            items = actionsBar.navigationItemsCollection?.items?.filterNotNull()
                                ?.map { item ->
                                    NavigationBar.Item(
                                        tile = NavigationBar.Item.Tile(
                                            title = item.tile?.name
                                                ?: "home nav item tile name missing",
                                            hash = item.tile?.slug
                                                ?: "nome-nav-item-tile-slug-missing"
                                        ),
                                        color = Color(
                                            item.color?.hex
                                                ?: "blue"
                                        ),
                                        iconUrl = item.icon?.url?.toURL()
                                            ?: URL("data://")
                                    )
                                } ?: emptyList()
                        )
                    }!!,
                    introduction = Content.Text(
                        document = homepage.introduction?.text?.toDocument(
                            preview
                        )
                            ?: RichText.Document(emptyList())
                    ),
                    preview = preview
                )
            }

            else -> throw IllegalArgumentException("no item in homepageCollection")
        }

    private suspend fun ApolloResponse<PageQuery.Data>.toPage(preview: Boolean): Page? =
        when (val page = data?.pageCollection?.items?.first()) {
            is PageQuery.Item -> {
                Page(
                    path = page.url ?: "page-url-missing",
                    title = page.name ?: "page name missing",
                    navigationBar = page.navigationBar?.let {
                        NavigationBar(
                            page = NavigationBar.Page(
                                path = "",
                                title = page.name
                                    ?: "page name missing"
                            ),
                            items = page.navigationBar.navigationItemsCollection?.items?.filterNotNull()
                                ?.map { item ->
                                    NavigationBar.Item(
                                        tile = NavigationBar.Item.Tile(
                                            title = item.tile?.name
                                                ?: "nav item tile title missing",
                                            hash = item.tile?.slug
                                                ?: "nav item tile slug missing"
                                        ),
                                        color = Color(
                                            item.color?.hex
                                                ?: "blue"
                                        ),
                                        iconUrl = item.icon?.url?.toURL()
                                            ?: URL("data://")
                                    )
                                } ?: emptyList()
                        )
                    },
                    tiles = page.tilesCollection?.items?.filterNotNull()
                        ?.map { tile ->
                            Tile(
                                hash = tile.slug
                                    ?: "tile hash missing",
                                title = tile.name
                                    ?: "tile title missing",
                                backgroundColor = Color(
                                    tile.color?.hex ?: "blue"
                                ),
                                layout = Tile.Layout.valueByType(
                                    tile.layout?.type
                                ),
                                body = Content.Text(
                                    document = tile.body?.text?.toDocument(
                                        preview
                                    )
                                        ?: RichText.Document(
                                            emptyList()
                                        ),
                                    color = tile.body?.textColor?.hex?.let {
                                        Color(
                                            it
                                        )
                                    },
                                    backgroundColor = tile.body?.backgroundColor?.hex?.let {
                                        Color(
                                            it
                                        )
                                    },
                                    borderColor = tile.body?.borderColor?.hex?.let {
                                        Color(
                                            it
                                        )
                                    }
                                ),
                                media = tile.media?.let { media ->
                                    when {
                                        media.youTubeVideosCollection?.items?.isNotEmpty() == true ->
                                            Content.Media.YouTube(
                                                videos = media.youTubeVideosCollection.items.map { video ->
                                                    Content.Media.YouTube.Video(
                                                        id = video!!.videoId!!,
                                                        title = video.name!!
                                                    )
                                                }
                                            )

                                        media.mediaCollection?.items?.any { it?.contentType == "application/zip" } == true ->
                                            media.mediaCollection.items.partition { it?.contentType != "application/zip" }
                                                .let { (images, archives) ->
                                                    Content.Media.Actions(
                                                        actions = images.zip(
                                                            archives
                                                        ) { image, archive ->
                                                            Content.Media.Actions.Action(
                                                                image = Image(
                                                                    url = URL(
                                                                        image?.url
                                                                            ?: "data://"
                                                                    ),
                                                                    title = image?.title
                                                                        ?: "gallery image title missing"
                                                                ),
                                                                archive = Archive(
                                                                    url = URL(
                                                                        archive?.url
                                                                            ?: "data://"
                                                                    ),
                                                                    title = archive?.title
                                                                        ?: "archive title missing"
                                                                )
                                                            )
                                                        }
                                                    )
                                                }

                                        else ->
                                            Content.Media.Gallery(
                                                images = media.mediaCollection?.items?.map { image ->
                                                    Image(
                                                        url = URL(
                                                            image?.url
                                                                ?: "data://"
                                                        ),
                                                        title = image?.title
                                                            ?: "gallery image title missing"
                                                    )
                                                }
                                                    ?: emptyList()
                                            )
                                    }
                                } ?: Content.Media.Gallery(
                                    emptyList()
                                ),
                                gimmicks = tile.gimmicksCollection?.items?.map { gimmick ->
                                    Tile.Gimmick(
                                        image = Image(
                                            url = URL(
                                                gimmick?.image?.url
                                                    ?: "data://"
                                            ),
                                            title = gimmick?.image?.title
                                                ?: "gimmick image title is missing"
                                        ),
                                        position = Tile.Gimmick.Position.valueByType(
                                            gimmick?.position?.type
                                        )
                                    )
                                } ?: emptyList()
                            )
                        } ?: emptyList(),
                    preview = preview
                )
            }

            else -> null
        }

    private suspend fun HomepageQuery.Text.toDocument(preview: Boolean): RichText.Document =
        json.toJsonElement().toDocument(
            mapToParagraph = { mapToParagraph(preview) },
            mapEmbeddedEntryToMedia = { mapEmbeddedEntryToMedia(links) },
            mapEmbeddedAssetToImage = { mapembeddedAssetToImage(links) }
        )

    private suspend fun PageQuery.Text.toDocument(preview: Boolean): RichText.Document =
        json.toJsonElement().toDocument(
            mapToParagraph = { mapToParagraph(preview) },
            mapEmbeddedEntryToMedia = { null },
            mapEmbeddedAssetToImage = { null }
        )

    private suspend fun JsonElement.toDocument(
        mapToParagraph: suspend JsonNode.Paragraph.() -> RichText.Paragraph,
        mapEmbeddedEntryToMedia: JsonNode.EmbeddedEntryBlock.() -> Content.Media?,
        mapEmbeddedAssetToImage: JsonNode.EmbeddedAssetBlock.() -> Image?
    ): RichText.Document {
        val documentFormat = Json {
            classDiscriminator = "nodeType"
            ignoreUnknownKeys = true
        }
        val jsonDocument: JsonNode.Document =
            documentFormat.decodeFromJsonElement(toJsonElement())
        return RichText.Document(
            nodes = jsonDocument.content.mapNotNull { jsonNode ->
                when (jsonNode) {
                    is JsonNode.Paragraph -> jsonNode.mapToParagraph()
                    is JsonNode.EmbeddedEntryBlock -> jsonNode.mapEmbeddedEntryToMedia()
                    is JsonNode.EmbeddedAssetBlock -> jsonNode.mapEmbeddedAssetToImage()
                }
            }
        )
    }

    private suspend fun JsonNode.Paragraph.mapToParagraph(preview: Boolean) =
        RichText.Paragraph(
            items = content.mapNotNull { node ->
                when (node) {
                    is JsonNode.Text -> node.value.takeIf { it.isNotBlank() }
                        ?.let { value ->
                            value.split("\n").map { line ->
                                RichText.Paragraph.Text(value = line.trim())
                            }
                        }

                    is JsonNode.Hyperlink -> listOf(
                        RichText.Paragraph.Hyperlink(
                            href = node.data.uri,
                            value = node.content.joinToString(" ") { it.value }
                        )
                    )

                    is JsonNode.AssetHyperlink -> when (
                        val result =
                            getAsset(node.data.target.sys.id, preview)
                    ) {
                        is Result.Success<Asset?> -> listOfNotNull(
                            result.value?.let {
                                RichText.Paragraph.Hyperlink(
                                    href = result.value.url.toString(),
                                    value = node.content.joinToString(
                                        " "
                                    ) { it.value }
                                )
                            }
                        )

                        else -> null
                    }
                }
            }.flatten()
        )

    private fun JsonNode.EmbeddedEntryBlock.mapEmbeddedEntryToMedia(links: HomepageQuery.Links) =
        when (
            val block =
                links.entries.block.find { it?.onContainer?.sys?.id == data.target.sys.id }
        ) {
            is HomepageQuery.Block -> when {
                block.onContainer?.mediaCollection?.items?.isNotEmpty() == true -> {
                    val container = block.onContainer
                    Content.Media.Gallery(
                        color = container.textColor?.hex?.let { Color(it) },
                        backgroundColor = container.backgroundColor?.hex?.let {
                            Color(
                                it
                            )
                        },
                        borderColor = container.borderColor?.hex?.let {
                            Color(
                                it
                            )
                        },
                        images = block.onContainer.mediaCollection.items.mapNotNull { image ->
                            image?.url?.let {
                                Image(
                                    url = URL(it),
                                    title = image.title!!
                                )
                            }
                        }
                    )
                }

                block.onContainer?.youTubeVideosCollection?.items?.isNotEmpty() == true -> {
                    val container = block.onContainer
                    Content.Media.YouTube(
                        color = container.textColor?.hex?.let { Color(it) },
                        backgroundColor = container.backgroundColor?.hex?.let {
                            Color(
                                it
                            )
                        },
                        borderColor = container.borderColor?.hex?.let {
                            Color(
                                it
                            )
                        },
                        videos = block.onContainer.youTubeVideosCollection.items.mapNotNull { video ->
                            video?.videoId?.let {
                                Content.Media.YouTube.Video(
                                    id = it,
                                    title = video.name!!
                                )
                            }
                        }
                    )
                }

                else ->
                    null
            }

            else ->
                null
        }

    private fun JsonNode.EmbeddedAssetBlock.mapembeddedAssetToImage(links: HomepageQuery.Links) =
        when (
            val asset = links.assets.block.find { it?.sys?.id == data.target.sys.id }
        ) {
            is HomepageQuery.Block1 ->
                Image(url = URL(asset.url!!), title = asset.title!!)

            else ->
                null
        }
}
